/**
 * VariantTabs
 * -----------
 * Renders the tab bar for switching between Base and A/B variant documents.
 *
 * Responsibilities:
 *  - Show "Base" tab always (when AB is disabled).
 *  - Show one tab per variant when AB is enabled.
 *  - "Enable A/B Testing" button when AB is not yet enabled.
 *  - "+ Add Variant" + gear ⚙ Settings button when AB is already enabled.
 *  - Remove (×) button on each variant tab — HIDDEN whenever there are 2
 *    or fewer variants.
 *  - Clicking ⚙ opens the A/B Test Settings modal.
 *    - Even allocation: evenly divides 100% across all variants.
 *    - Custom allocation: multi-handle range slider with (N-1) handles.
 *  - Highlight the active tab.
 *  - Delegate all state mutations to useVariantEditor via props.
 */

import { useState, useCallback, ReactNode } from "react";
import { Button, Modal, Tooltip } from "@shopify/polaris";
import type { EditorVariant } from "../../hooks/useVariantEditor";
import { PlusIcon, SettingsIcon } from "@shopify/polaris-icons";
import CustomTooltipAdmin from "../CustomTooltipAdmin/CustomTooltipAdmin";
import useBundleMixpanelHook from "../../hooks/useBundleMixpanelHook";

interface VariantTabsProps {
    activeTarget: string;
    variants: EditorVariant[];
    abEnabled: boolean;
    /** ab_config from base doc – contains traffic_allocation_type and traffic_percentage */
    abConfig?: {
        traffic_allocation_type?: "even" | "custom";
        traffic_percentage?: Record<string, number>;
    };
    onSwitchVariant: (targetId: string) => Promise<boolean>;
    onAddVariant: () => void;
    onRemoveVariant: (variantId: string) => void;
    /** Called when user saves the A/B Test Settings modal */
    onUpdateABConfig: (allocationType: "even" | "custom", customPercentages?: Record<string, number>) => void;
    validationErrorCount?: number;
    /** True when the store's current plan doesn't include A/B testing */
    abTestingLocked?: boolean;
    lockedTooltipContent: string | ReactNode;
    bundleType: string;
}

const MIN_VARIANTS_FOR_AB = 2;

export function VariantTabs({
    activeTarget,
    variants,
    abEnabled,
    abConfig,
    onSwitchVariant,
    onAddVariant,
    onRemoveVariant,
    onUpdateABConfig,
    validationErrorCount = 0,
    abTestingLocked = false,
    lockedTooltipContent,
    bundleType,
}: VariantTabsProps) {
    const [settingsOpen, setSettingsOpen] = useState(false);
    const { trackCtaClick } = useBundleMixpanelHook();

    const handleSwitch = async (targetId: string) => {
        trackCtaClick({
            cta: "AB Variant Tab",
            ctaType: targetId,
            bundleType,
        });
        const succeeded = await onSwitchVariant(targetId);
        if (!succeeded && validationErrorCount > 0) {
            // The UI will show validation errors in the form
        }
    };

    const canRemoveVariant = variants.length > MIN_VARIANTS_FOR_AB && !abTestingLocked;

    if (!abEnabled) return null;

    return (
        <>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "12px 16px",
                    borderBottom: "1px solid #e1e3e5",
                    backgroundColor: "#f6f6f7",
                    flexWrap: "wrap",
                }}
            >
                {/* Variant tabs */}
                {variants.map((v, index) => (
                    <VariantTab
                        key={v.id}
                        isActive={activeTarget === v.id}
                        canRemove={canRemoveVariant}
                        onSwitch={() => handleSwitch(v.id)}
                        onRemove={() => onRemoveVariant(v.id)}
                        index={index}
                    />
                ))}

                {variants?.length < 4 && (
                    <CustomTooltipAdmin content={lockedTooltipContent}>
                        <Tooltip content="Add Variant">
                            <Button
                                size="slim"
                                onClick={abTestingLocked ? undefined : onAddVariant}
                                variant="secondary"
                                icon={PlusIcon}
                                disabled={abTestingLocked}
                            />
                        </Tooltip>
                    </CustomTooltipAdmin>
                )}

                <CustomTooltipAdmin content={lockedTooltipContent}>
                    <div style={{ marginLeft: "auto" }}>
                        <Tooltip content="Settings">
                            <Button
                                icon={SettingsIcon}
                                onClick={
                                    abTestingLocked
                                        ? undefined
                                        : () => {
                                              trackCtaClick({
                                                  cta: "AB Settings",
                                                  ctaType: "Click",
                                                  bundleType,
                                              });
                                              setSettingsOpen(true);
                                          }
                                }
                                variant="secondary"
                                disabled={abTestingLocked}
                            />
                        </Tooltip>
                    </div>
                </CustomTooltipAdmin>
            </div>

            {/* Settings modal */}
            {settingsOpen && (
                <ABTestSettingsModal
                    variants={variants}
                    abConfig={abConfig}
                    onSave={(type, pct) => {
                        onUpdateABConfig(type, pct);
                        setSettingsOpen(false);
                    }}
                    onClose={() => setSettingsOpen(false)}
                />
            )}
        </>
    );
}

// ---------------------------------------------------------------------------
// A/B Test Settings Modal
// ---------------------------------------------------------------------------

interface ABTestSettingsModalProps {
    variants: EditorVariant[];
    abConfig?: {
        traffic_allocation_type?: "even" | "custom";
        traffic_percentage?: Record<string, number>;
    };
    onSave: (type: "even" | "custom", customPercentages?: Record<string, number>) => void;
    onClose: () => void;
}

function computeEven(variants: EditorVariant[]): Record<string, number> {
    const N = variants.length;
    const base = Math.floor(100 / N);
    const remainder = 100 - base * N;
    const result: Record<string, number> = {};
    variants.forEach((v, i) => {
        result[v.id] = base + (i === N - 1 ? remainder : 0);
    });
    return result;
}

function ABTestSettingsModal({ variants, abConfig, onSave, onClose }: ABTestSettingsModalProps) {
    const [allocType, setAllocType] = useState<"even" | "custom">(abConfig?.traffic_allocation_type ?? "even");

    const initialEven = computeEven(variants);
    const [customPct, setCustomPct] = useState<Record<string, number>>(() => {
        const existing = abConfig?.traffic_percentage;
        const matchesCurrentVariants =
            !!existing &&
            Object.keys(existing).length === variants.length &&
            variants.every((v) => typeof existing[v.id] === "number");
        return matchesCurrentVariants ? existing : initialEven;
    });

    const displayPct = allocType === "even" ? initialEven : customPct;

    const handleSave = useCallback(() => {
        if (allocType === "even") {
            onSave("even");
        } else {
            onSave("custom", customPct);
        }
    }, [allocType, customPct, onSave]);

    // The slider encodes (N-1) breakpoints in [0, 100] cumulative space.
    // breakpoints[0] = split between variant[0] and variant[1], etc.
    const N = variants.length;
    const breakpointsFromPct = (pct: Record<string, number>): number[] => {
        const bps: number[] = [];
        let cum = 0;
        for (let i = 0; i < N - 1; i++) {
            cum += pct[variants[i].id] ?? 0;
            bps.push(cum);
        }
        return bps;
    };
    const pctFromBreakpoints = (bps: number[]): Record<string, number> => {
        const result: Record<string, number> = {};
        let prev = 0;
        for (let i = 0; i < N; i++) {
            const curr = i < N - 1 ? bps[i] : 100;
            result[variants[i].id] = Math.max(1, Math.round(curr - prev));
            prev = curr;
        }
        // Fix rounding drift
        const total = Object.values(result).reduce((a, b) => a + b, 0);
        if (total !== 100) result[variants[N - 1].id] += 100 - total;
        return result;
    };

    const [breakpoints, setBreakpoints] = useState<number[]>(() => breakpointsFromPct(customPct));
    const [dragging, setDragging] = useState<number | null>(null);

    const handleSliderMouseDown = (index: number) => (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragging(index);
    };

    const handleSliderMouseMove = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (dragging === null) return;
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const raw = Math.round(((e.clientX - rect.left) / rect.width) * 100);
            const clamped = Math.max(
                dragging === 0 ? 1 : breakpoints[dragging - 1] + 1,
                Math.min(dragging === N - 2 ? 99 : breakpoints[dragging + 1] - 1, raw),
            );
            const newBps = [...breakpoints];
            newBps[dragging] = clamped;
            setBreakpoints(newBps);
            const newPct = pctFromBreakpoints(newBps);
            setCustomPct(newPct);
        },
        [dragging, breakpoints, N],
    );

    const handleSliderMouseUp = () => setDragging(null);

    const VARIANT_COLORS = ["#005bd3", "#6e5bdd", "#c54083", "#de6f26", "#2a9d5c", "#1d8c8c"];

    return (
        <Modal
            open
            onClose={onClose}
            title="A/B Test Settings"
            primaryAction={{ content: "Save settings", onAction: handleSave }}
            secondaryActions={[{ content: "Cancel", onAction: onClose }]}
        >
            <Modal.Section>
                {/* Variant percentage bubbles */}
                <div style={{ display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap" }}>
                    {variants.map((v, i) => (
                        <div
                            key={v.id}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "4px",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color: "#6d7175",
                                }}
                            >
                                {displayPct[v.id] ?? 0}%
                            </span>
                            <div
                                style={{
                                    width: "44px",
                                    height: "44px",
                                    borderRadius: "50%",
                                    border: `2px solid ${VARIANT_COLORS[i % VARIANT_COLORS.length]}`,
                                    backgroundColor: `${VARIANT_COLORS[i % VARIANT_COLORS.length]}18`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 700,
                                    fontSize: "16px",
                                    color: VARIANT_COLORS[i % VARIANT_COLORS.length],
                                }}
                            >
                                {String.fromCharCode(65 + i)}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Traffic allocation type */}
                <p style={{ fontSize: "13px", fontWeight: 600, color: "#303030", marginBottom: "12px" }}>
                    Traffic allocation
                </p>

                <label
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        cursor: "pointer",
                        marginBottom: "10px",
                    }}
                >
                    <input
                        type="radio"
                        name="alloc"
                        checked={allocType === "even"}
                        onChange={() => setAllocType("even")}
                        style={{ marginTop: "2px", accentColor: "#005bd3" }}
                    />
                    <div>
                        <span style={{ fontSize: "13px", fontWeight: 500, color: "#303030" }}>Even allocation</span>
                        <p style={{ fontSize: "12px", color: "#6d7175", margin: "2px 0 0" }}>
                            Traffic is split equally across all variants
                        </p>
                    </div>
                </label>

                <label
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        cursor: "pointer",
                        marginBottom: "20px",
                    }}
                >
                    <input
                        type="radio"
                        name="alloc"
                        checked={allocType === "custom"}
                        onChange={() => {
                            setAllocType("custom");
                            setBreakpoints(breakpointsFromPct(customPct));
                        }}
                        style={{ marginTop: "2px", accentColor: "#005bd3" }}
                    />
                    <div>
                        <span style={{ fontSize: "13px", fontWeight: 500, color: "#303030" }}>Custom allocation</span>
                        <p style={{ fontSize: "12px", color: "#6d7175", margin: "2px 0 0" }}>
                            Choose what percentage of traffic is sent into variants
                        </p>
                    </div>
                </label>

                {/* Multi-handle range slider (shown only in custom mode) */}
                {allocType === "custom" && (
                    <div style={{ paddingBottom: "8px" }}>
                        <div
                            style={{
                                position: "relative",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                userSelect: "none",
                            }}
                            onMouseMove={handleSliderMouseMove}
                            onMouseUp={handleSliderMouseUp}
                            onMouseLeave={handleSliderMouseUp}
                        >
                            {/* Coloured track segments */}
                            {variants.map((v, i) => {
                                const leftPct = i === 0 ? 0 : breakpoints[i - 1];
                                const rightPct = i < N - 1 ? breakpoints[i] : 100;
                                return (
                                    <div
                                        key={v.id}
                                        style={{
                                            position: "absolute",
                                            left: `${leftPct}%`,
                                            width: `${rightPct - leftPct}%`,
                                            height: "8px",
                                            backgroundColor: VARIANT_COLORS[i % VARIANT_COLORS.length],
                                            borderRadius: i === 0 ? "4px 0 0 4px" : i === N - 1 ? "0 4px 4px 0" : "0",
                                        }}
                                    />
                                );
                            })}

                            {/* Drag handles (N-1 handles) */}
                            {breakpoints.map((bp, idx) => (
                                <div
                                    key={idx}
                                    onMouseDown={handleSliderMouseDown(idx)}
                                    style={{
                                        position: "absolute",
                                        left: `${bp}%`,
                                        transform: "translateX(-50%)",
                                        width: "18px",
                                        height: "18px",
                                        borderRadius: "50%",
                                        backgroundColor: "#fff",
                                        border: "2px solid #303030",
                                        boxShadow: "0 1px 4px #0003",
                                        cursor: "ew-resize",
                                        zIndex: 2,
                                    }}
                                />
                            ))}
                        </div>

                        {/* Percentage labels under each segment */}
                        <div style={{ display: "flex", marginTop: "12px", gap: "8px", flexWrap: "wrap" }}>
                            {variants.map((v, i) => (
                                <div
                                    key={v.id}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        padding: "4px 10px",
                                        borderRadius: "20px",
                                        backgroundColor: `${VARIANT_COLORS[i % VARIANT_COLORS.length]}18`,
                                        border: `1px solid ${VARIANT_COLORS[i % VARIANT_COLORS.length]}40`,
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        color: VARIANT_COLORS[i % VARIANT_COLORS.length],
                                    }}
                                >
                                    {String.fromCharCode(65 + i)}: {displayPct[v.id] ?? 0}%
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Modal.Section>
        </Modal>
    );
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

interface VariantTabProps {
    index: number;
    isActive: boolean;
    canRemove: boolean;
    onSwitch: () => void;
    onRemove: () => void;
}

function VariantTab({ index, isActive, canRemove, onSwitch, onRemove }: VariantTabProps) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "6px 10px",
                borderRadius: "6px",
                border: isActive ? "2px solid #005bd3" : "2px solid transparent",
                backgroundColor: isActive ? "#e8f0fd" : "transparent",
                transition: "all 0.15s ease",
            }}
        >
            <button
                onClick={onSwitch}
                style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    fontWeight: isActive ? 600 : 400,
                    fontSize: "13px",
                    color: isActive ? "#005bd3" : "#303030",
                }}
            >
                Variant {String.fromCharCode(65 + index)}
            </button>
            {canRemove && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    title={`Remove Variant ${String.fromCharCode(65 + index)}`}
                    style={{
                        background: "none",
                        border: "none",
                        padding: "0 2px",
                        cursor: "pointer",
                        color: "#6d7175",
                        fontSize: "14px",
                        lineHeight: 1,
                    }}
                >
                    ×
                </button>
            )}
        </div>
    );
}
